import { useState, useEffect } from "react";
import { apiGet, apiPost, apiPut } from "@/lib/api";
import { Search, UserX, ShieldOff, ShieldCheck, FilterX, AlertTriangle, Trash2, Plus } from "lucide-react";

const CATEGORIES = ["", "Guest", "Contractor", "Delivery", "Domestic Helper"];
const STATUSES = ["", "checked_in", "checked_out", "pending_approval", "approved", "denied", "overstay"];

export default function VisitorLog() {
  const [tab, setTab] = useState("log");
  const [logs, setLogs] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", category: "", unit: "", date_from: "", date_to: "" });

  useEffect(() => {
    if (tab === "log") loadLogs();
    else loadBlacklist();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadLogs() {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    try { const r = await apiGet(`/visits?${params.toString()}`); setLogs(r.data); } catch {}
    setLoading(false);
  }

  async function loadBlacklist() {
    setLoading(true);
    try { const r = await apiGet("/blacklist"); setBlacklist(r.data); } catch {}
    setLoading(false);
  }

  function upd(k) { return e => setFilters(f => ({ ...f, [k]: e.target.value })); }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className={`btn ${tab === "log" ? "btn-primary" : ""}`} onClick={() => setTab("log")}>Visit Log</button>
        <button className={`btn ${tab === "blacklist" ? "btn-primary" : ""}`} onClick={() => setTab("blacklist")}>
          <UserX size={13} style={{ marginRight: 4 }} />Blacklist
          {blacklist.length > 0 && <span className="nav-badge warn" style={{ marginLeft: 4 }}>{blacklist.length}</span>}
        </button>
      </div>

      {tab === "log" && (
        <>
          {/* Filters */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, padding: "12px 14px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
            <select className="form-input" style={{ width: 130, fontSize: 12 }} value={filters.status} onChange={upd("status")}>
              {STATUSES.map(s => <option key={s} value={s}>{s || "All Statuses"}</option>)}
            </select>
            <select className="form-input" style={{ width: 140, fontSize: 12 }} value={filters.category} onChange={upd("category")}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c || "All Categories"}</option>)}
            </select>
            <input className="form-input" style={{ width: 110, fontSize: 12 }} placeholder="Unit" value={filters.unit} onChange={upd("unit")} />
            <input type="date" className="form-input" style={{ width: 130, fontSize: 12 }} value={filters.date_from} onChange={upd("date_from")} />
            <input type="date" className="form-input" style={{ width: 130, fontSize: 12 }} value={filters.date_to} onChange={upd("date_to")} />
            <button className="btn btn-primary" onClick={loadLogs} style={{ fontSize: 12 }}>
              <Search size={12} style={{ marginRight: 4 }} />Filter
            </button>
            <button className="btn" onClick={() => { setFilters({ status: "", category: "", unit: "", date_from: "", date_to: "" }); }}
              style={{ fontSize: 12 }} title="Clear filters">
              <FilterX size={12} />
            </button>
          </div>

          {loading ? <div style={{ fontSize: 13, color: "var(--text2)" }}>Loading...</div> : <LogTable logs={logs} onBlacklist={loadLogs} />}
        </>
      )}

      {tab === "blacklist" && (
        <BlacklistPanel blacklist={blacklist} onRefresh={loadBlacklist} />
      )}
    </div>
  );
}

// ── Log Table ─────────────────────────────────────────────────────────────────
function LogTable({ logs, onBlacklist }) {
  const STATUS_STYLE = {
    checked_in: { color: "#16a34a", bg: "#dcfce7" },
    checked_out: { color: "#6b7280", bg: "#f3f4f6" },
    overstay: { color: "#d97706", bg: "#fef3c7" },
    denied: { color: "#dc2626", bg: "#fee2e2" },
    pending_approval: { color: "#7c3aed", bg: "#ede9fe" },
    approved: { color: "#16a34a", bg: "#dcfce7" },
  };

  const [blacklistModal, setBlacklistModal] = useState(null);

  if (!logs.length) return <div style={{ fontSize: 13, color: "var(--text2)", padding: "20px 0" }}>No visits found for the selected filters.</div>;

  return (
    <>
      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8 }}>{logs.length} record(s)</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 700 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
              {["Date/Time", "Visitor", "IC", "Plate", "Unit", "Category", "Purpose", "Duration", "Status", ""].map(h => (
                <th key={h} style={{ padding: "6px 8px", fontWeight: 600, fontSize: 11, color: "var(--text2)", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map(v => {
              const d = v.details;
              const st = STATUS_STYLE[v.status] ?? STATUS_STYLE.checked_out;
              const checkinTime = d.checked_in_at ? new Date(d.checked_in_at).toLocaleString("en-MY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
              const duration = d.checked_in_at && d.checked_out_at
                ? Math.round((new Date(d.checked_out_at) - new Date(d.checked_in_at)) / 60000) + "m"
                : d.checked_in_at ? "On-site" : "—";
              return (
                <tr key={v.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                  <td style={{ padding: "7px 8px", whiteSpace: "nowrap", fontSize: 11 }}>{checkinTime}</td>
                  <td style={{ padding: "7px 8px", fontWeight: 500 }}>{d.visitor_name}</td>
                  <td style={{ padding: "7px 8px", fontSize: 11, color: "var(--text2)" }}>{d.visitor_ic || "—"}</td>
                  <td style={{ padding: "7px 8px" }}>
                    {d.vehicle_plate ? (
                      <span style={{ background: "#fef9c3", color: "#854d0e", padding: "2px 7px", borderRadius: 4, fontWeight: 700, fontSize: 11, letterSpacing: 1 }}>{d.vehicle_plate}</span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "7px 8px" }}>{d.unit}</td>
                  <td style={{ padding: "7px 8px" }}>{d.category}</td>
                  <td style={{ padding: "7px 8px", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.purpose}</td>
                  <td style={{ padding: "7px 8px" }}>{duration}</td>
                  <td style={{ padding: "7px 8px" }}>
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: st.bg, color: st.color, fontWeight: 600 }}>{v.status.replace(/_/g, " ")}</span>
                  </td>
                  <td style={{ padding: "7px 8px" }}>
                    {d.visitor_ic && (
                      <button className="btn" style={{ fontSize: 11, padding: "2px 8px" }} title="Add to blacklist"
                        onClick={() => setBlacklistModal({ ic: d.visitor_ic, name: d.visitor_name })}>
                        <ShieldOff size={11} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {blacklistModal && (
        <AddBlacklistModal ic={blacklistModal.ic} name={blacklistModal.name}
          onClose={() => setBlacklistModal(null)}
          onDone={() => { setBlacklistModal(null); onBlacklist(); }} />
      )}
    </>
  );
}

// ── Blacklist Panel ───────────────────────────────────────────────────────────
function BlacklistPanel({ blacklist, onRefresh }) {
  const [addModal, setAddModal] = useState(false);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: "var(--text2)" }}>{blacklist.length} blacklisted visitor(s)</div>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setAddModal(true)}>
          <Plus size={12} style={{ marginRight: 4 }} />Add to Blacklist
        </button>
      </div>

      {!blacklist.length ? (
        <div style={{ fontSize: 13, color: "var(--text2)", padding: "20px 0" }}>No blacklisted visitors.</div>
      ) : (
        blacklist.map(b => (
          <div key={b.id} style={{ border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 14px", marginBottom: 10, background: "#fff5f5" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  <UserX size={13} color="#dc2626" />{b.name}
                </div>
                <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>IC: {b.ic_number}</div>
                {b.reason && <div style={{ fontSize: 12, marginTop: 4 }}><b>Reason:</b> {b.reason}</div>}
                <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 3 }}>Added {new Date(b.created_at).toLocaleDateString("en-MY")}</div>
              </div>
              <button className="btn" style={{ fontSize: 12, background: "#dcfce7", color: "#15803d" }}
                onClick={async () => { await apiPut(`/blacklist/${b.id}/remove`); onRefresh(); }}>
                <ShieldCheck size={12} style={{ marginRight: 4 }} />Remove
              </button>
            </div>
          </div>
        ))
      )}

      {addModal && <AddBlacklistModal onClose={() => setAddModal(false)} onDone={() => { setAddModal(false); onRefresh(); }} />}
    </div>
  );
}

// ── Add Blacklist Modal ────────────────────────────────────────────────────────
function AddBlacklistModal({ ic = "", name = "", onClose, onDone }) {
  const [form, setForm] = useState({ ic_number: ic, name, reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  function upd(k) { return e => setForm(f => ({ ...f, [k]: e.target.value })); }
  async function submit(e) {
    e.preventDefault(); setError(""); setSubmitting(true);
    try {
      await apiPost("/blacklist", { ic_number: form.ic_number, name: form.name, reason: form.reason });
      onDone();
    } catch (e) { setError(e.response?.data?.detail ?? "Failed"); }
    finally { setSubmitting(false); }
  }
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 400, padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 6 }}>
            <UserX size={14} color="#dc2626" />Add to Blacklist
          </div>
          <button className="btn" style={{ fontSize: 12 }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: "8px 12px", background: "#fef3c7", borderRadius: 6, fontSize: 12, marginBottom: 14, display: "flex", gap: 6 }}>
          <AlertTriangle size={13} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
          This visitor will be flagged at all future entry attempts.
        </div>
        {error && <div className="auth-error" style={{ marginBottom: 10 }}>{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">IC / Passport *</label>
            <input className="form-input" value={form.ic_number} onChange={upd("ic_number")} required />
          </div>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" value={form.name} onChange={upd("name")} required />
          </div>
          <div className="form-group">
            <label className="form-label">Reason for Blacklisting</label>
            <textarea className="form-input" rows={3} value={form.reason} onChange={upd("reason")} placeholder="e.g. Suspicious behavior on 12 Jun 2026" />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" style={{ flex: 2, background: "#dc2626", color: "#fff" }} disabled={submitting}>
              {submitting ? "Adding..." : "Confirm Blacklist"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
